import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Brain, Send, Trash2, Check, X, Mic, MicOff, Loader2,
  CheckCircle2, FileText, AlertTriangle, Bell, Target,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  task:        { icon: Check,        label: "Task",        color: "bg-blue-500/15 text-blue-400 border-blue-500/40" },
  sprint_item: { icon: Target,       label: "Sprint",      color: "bg-purple-500/15 text-purple-400 border-purple-500/40" },
  note:        { icon: FileText,     label: "Note",        color: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40" },
  incident:    { icon: AlertTriangle, label: "Incident",   color: "bg-red-500/15 text-red-400 border-red-500/40" },
  reminder:    { icon: Bell,         label: "Reminder",    color: "bg-amber-500/15 text-amber-400 border-amber-500/40" },
};

export default function BrainDumpModal({ open, onClose, onShipped }) {
  const [stage, setStage] = useState("input"); // input | triaging | review | done
  const [text, setText] = useState("");
  const [items, setItems] = useState([]);
  const [shipped, setShipped] = useState(new Set());
  const [skipped, setSkipped] = useState(new Set());
  const [working, setWorking] = useState(null); // id of item currently being shipped
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setStage("input");
      setText("");
      setItems([]);
      setShipped(new Set());
      setSkipped(new Set());
      setListening(false);
    }
  }, [open]);

  // Voice input via Web Speech API (Chrome/Edge/Safari)
  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice input not supported in this browser");
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const recog = new SR();
    recog.continuous = true;
    recog.interimResults = false;
    recog.lang = "en-US";
    recog.onresult = (ev) => {
      let chunk = "";
      for (let i = ev.resultIndex; i < ev.results.length; i += 1) {
        chunk += ev.results[i][0].transcript + " ";
      }
      setText((prev) => (prev ? prev + " " : "") + chunk.trim());
    };
    recog.onerror = () => { setListening(false); };
    recog.onend = () => { setListening(false); };
    recog.start();
    recognitionRef.current = recog;
    setListening(true);
  };

  const triage = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      toast.error("Nothing to triage — dump something first");
      return;
    }
    setStage("triaging");
    try {
      const res = await axios.post(
        `${API}/brain-dump/triage`,
        { text: trimmed },
        { withCredentials: true }
      );
      const parsed = res.data.items || [];
      if (parsed.length === 0) {
        toast.error("Niko couldn't find anything actionable in that");
        setStage("input");
        return;
      }
      setItems(parsed.map((it, idx) => ({ ...it, _id: `dump_${idx}` })));
      setStage("review");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Triage failed");
      setStage("input");
    }
  };

  const shipItem = async (item) => {
    if (shipped.has(item._id) || skipped.has(item._id)) return;
    setWorking(item._id);
    try {
      if (item.kind === "task" || item.kind === "reminder") {
        await axios.post(
          `${API}/tasks`,
          {
            description: item.text,
            category: item.category || (item.kind === "reminder" ? "Personal" : "Admin"),
            priority: item.priority || (item.kind === "reminder" ? "ThisWeek" : "Today"),
            assigned_to: item.assignee || "self",
            deadline: item.deadline || null,
          },
          { withCredentials: true }
        );
      } else if (item.kind === "sprint_item") {
        const week = new Date().toISOString().slice(0, 10);
        await axios.post(
          `${API}/sprint-items`,
          {
            sprint_week: week,
            area_name: item.category || "Strategic",
            task_text: item.text,
            send_to_tasks: true,
            task_priority: item.priority || "ThisWeek",
          },
          { withCredentials: true }
        );
      } else if (item.kind === "incident") {
        await axios.post(
          `${API}/incidents`,
          {
            title: item.text,
            incident_type: "Other",
            unit: item.text.match(/unit\s*(\d+)/i)?.[0] || "Unknown",
            incident_time: new Date().toISOString(),
            narrative: item.rationale || null,
          },
          { withCredentials: true }
        );
      } else if (item.kind === "note") {
        // Notes become ParkingLot tasks (lightweight)
        await axios.post(
          `${API}/tasks`,
          {
            description: `📝 ${item.text}`,
            category: "Admin",
            priority: "ParkingLot",
            assigned_to: "self",
          },
          { withCredentials: true }
        );
      }
      setShipped((s) => new Set(s).add(item._id));
      toast.success(`Sent: ${item.text.slice(0, 40)}`);
      onShipped?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setWorking(null);
    }
  };

  const skipItem = (item) => {
    setSkipped((s) => new Set(s).add(item._id));
  };

  const shipAll = async () => {
    for (const it of items) {
      if (!shipped.has(it._id) && !skipped.has(it._id)) {
        // eslint-disable-next-line no-await-in-loop
        await shipItem(it);
      }
    }
  };

  const remaining = items.filter(
    (i) => !shipped.has(i._id) && !skipped.has(i._id)
  ).length;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="brain-dump-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-400" />
            Brain Dump
            {stage === "review" && (
              <Badge variant="outline" className="ml-2 text-xs">
                {remaining} to decide · {shipped.size} sent
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Dump stream-of-consciousness text; Niko parses it into structured items you confirm individually.
          </DialogDescription>
        </DialogHeader>

        {stage === "input" && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Dump everything on your mind — no structure, no order. Niko will
              sort it into tasks, sprints, incidents, notes, and reminders.
            </p>
            <Textarea
              data-testid="brain-dump-text"
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Remind me to call Sarah about the audit, need to grab milk, cousin's birthday Saturday, JC prep meeting, patient on 400 had a fall last night..."
              rows={8}
              className="resize-none"
            />
            <div className="flex items-center justify-between gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleVoice}
                data-testid="brain-dump-voice-btn"
                className={listening ? "border-red-500 text-red-400" : ""}
              >
                {listening ? <MicOff className="w-4 h-4 mr-1" /> : <Mic className="w-4 h-4 mr-1" />}
                {listening ? "Stop" : "Voice"}
              </Button>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  data-testid="brain-dump-triage-btn"
                  onClick={triage}
                  disabled={!text.trim()}
                >
                  <Send className="w-4 h-4 mr-1" />
                  Triage
                </Button>
              </div>
            </div>
          </div>
        )}

        {stage === "triaging" && (
          <div className="py-10 flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            <p className="text-sm text-muted-foreground">
              Niko is sorting your dump...
            </p>
          </div>
        )}

        {stage === "review" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Tap Send on each item, or Send all.</span>
              {remaining > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={shipAll}
                  data-testid="brain-dump-ship-all"
                >
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Send all
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {items.map((item) => {
                const meta = KIND_META[item.kind] || KIND_META.task;
                const Icon = meta.icon;
                const isShipped = shipped.has(item._id);
                const isSkipped = skipped.has(item._id);
                const isWorking = working === item._id;
                return (
                  <Card
                    key={item._id}
                    className={`p-3 ${isShipped ? "opacity-50 bg-emerald-500/5" : ""} ${isSkipped ? "opacity-40" : ""}`}
                    data-testid={`brain-dump-item-${item.kind}`}
                  >
                    <div className="flex items-start gap-2">
                      <Badge variant="outline" className={`${meta.color} flex-shrink-0 mt-0.5`}>
                        <Icon className="w-3 h-3 mr-1" /> {meta.label}
                      </Badge>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{item.text}</p>
                        <div className="flex gap-2 mt-1 flex-wrap">
                          {item.category && (
                            <span className="text-[10px] text-muted-foreground">{item.category}</span>
                          )}
                          {item.priority && (
                            <Badge variant="outline" className="text-[10px] h-4 px-1">
                              {item.priority}
                            </Badge>
                          )}
                          {item.deadline && (
                            <span className="text-[10px] text-amber-400">Due {item.deadline}</span>
                          )}
                        </div>
                        {item.rationale && (
                          <p className="text-[10px] text-muted-foreground italic mt-1">
                            {item.rationale}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {!isShipped && !isSkipped && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => skipItem(item)}
                              data-testid="brain-dump-skip-btn"
                              className="h-7 w-7 p-0"
                              title="Skip"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => shipItem(item)}
                              disabled={isWorking}
                              data-testid="brain-dump-send-btn"
                              className="h-7 px-2"
                            >
                              {isWorking ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <>
                                  <Send className="w-3 h-3 mr-1" /> Send
                                </>
                              )}
                            </Button>
                          </>
                        )}
                        {isShipped && (
                          <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40">
                            <Check className="w-3 h-3 mr-1" /> Sent
                          </Badge>
                        )}
                        {isSkipped && (
                          <Badge variant="outline" className="text-muted-foreground">
                            Skipped
                          </Badge>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
            <div className="flex justify-between pt-2 border-t border-zinc-800">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setStage("input"); setItems([]); }}
              >
                <Trash2 className="w-3 h-3 mr-1" /> Start over
              </Button>
              <Button onClick={onClose} data-testid="brain-dump-close-btn">
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
